import * as React from "react";
import {
  Box,
  Button,
  InputAdornment,
  MenuItem,
  Select,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

// A compact filter toolbar.
//
// The old screens stacked a caption label above every control, which cost a
// whole row of height per filter and still left no way to tell which filters
// were actually applied. Here the label lives inside the control as a muted
// prefix, and an applied control tints itself, so the bar reads at a glance
// and collapses to a single row.

const CONTROL_HEIGHT = 38;

// Shared shape for every control in the bar. `active` drives the applied tint.
const controlSx = (active) => ({
  height: CONTROL_HEIGHT,
  borderRadius: 2,
  fontSize: "0.875rem",
  backgroundColor: (t) =>
    active
      ? alpha(t.palette.primary.main, t.palette.mode === "dark" ? 0.2 : 0.07)
      : t.palette.background.field,
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: (t) => (active ? t.palette.primary.main : t.palette.border.default),
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: (t) => (active ? t.palette.primary.main : t.palette.border.strong),
  },
  "& .MuiSelect-icon": {
    color: (t) => (active ? t.palette.primary.main : t.palette.text.disabled),
  },
});

// "Project" when unset, "Project: Acme" when set — the label doubles as the
// placeholder so no caption row is needed.
function ControlValue({ label, value }) {
  return (
    <Box component="span" sx={{ display: "inline-flex", gap: 0.5, alignItems: "baseline" }}>
      <Box
        component="span"
        sx={{ color: "text.secondary", fontWeight: 500, whiteSpace: "nowrap" }}
      >
        {label}
        {value ? ":" : ""}
      </Box>
      {value && (
        <Box
          component="span"
          sx={{
            color: "text.primary",
            fontWeight: 600,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {value}
        </Box>
      )}
    </Box>
  );
}

export function FilterBar({ children, sx }) {
  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: 1,
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

export function FilterSearch({ value, onChange, placeholder = "Search", sx }) {
  return (
    <TextField
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      aria-label={placeholder}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon sx={{ fontSize: 18, color: "text.disabled" }} />
          </InputAdornment>
        ),
        // Only offer the clear affordance once there is something to clear.
        endAdornment: value ? (
          <InputAdornment position="end">
            <Box
              component="button"
              type="button"
              aria-label="Clear search"
              onClick={() => onChange("")}
              sx={{
                border: 0,
                background: "none",
                cursor: "pointer",
                display: "flex",
                p: 0,
                color: "text.disabled",
                "&:hover": { color: "text.primary" },
              }}
            >
              <CloseIcon sx={{ fontSize: 16 }} />
            </Box>
          </InputAdornment>
        ) : null,
        sx: controlSx(Boolean(value)),
      }}
      sx={{ flex: "1 1 220px", minWidth: 180, maxWidth: { sm: 320 }, ...sx }}
    />
  );
}

export function FilterSelect({
  label,
  value,
  onChange,
  options = [],
  allLabel = "All",
  disabled = false,
  minWidth = 150,
  sx,
}) {
  const active = value !== "" && value != null;
  const selected = options.find((o) => String(o.value) === String(value));

  return (
    <Select
      value={value ?? ""}
      onChange={onChange}
      disabled={disabled}
      displayEmpty
      aria-label={label}
      IconComponent={KeyboardArrowDownIcon}
      renderValue={() => <ControlValue label={label} value={selected?.label} />}
      MenuProps={{ PaperProps: { sx: { maxHeight: 320, minWidth } } }}
      sx={{ minWidth, ...controlSx(active), ...sx }}
    >
      <MenuItem value="">
        <Typography variant="body2" color="text.secondary">
          {allLabel}
        </Typography>
      </MenuItem>
      {options.map((option) => (
        <MenuItem key={option.value} value={option.value}>
          <Typography variant="body2" noWrap>
            {option.label}
          </Typography>
        </MenuItem>
      ))}
    </Select>
  );
}

export function FilterDate({ label, value, onChange, sx }) {
  const active = Boolean(value);

  return (
    <TextField
      type="date"
      value={value || ""}
      onChange={onChange}
      aria-label={label}
      // A native date input has no placeholder, so the label sits beside it.
      label={undefined}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", fontWeight: 500, whiteSpace: "nowrap" }}
            >
              {label}
            </Typography>
          </InputAdornment>
        ),
        sx: controlSx(active),
      }}
      sx={{ width: 190, "& input": { fontSize: "0.875rem", fontWeight: active ? 600 : 400 }, ...sx }}
    />
  );
}

// A segmented control. Preferred over a dropdown for a short, fixed set of
// modes: picking one costs a single click instead of open-then-choose, and the
// available options stay visible instead of hiding behind a menu.
export function FilterToggle({ value, onChange, options = [], size = "small", sx }) {
  return (
    <ToggleButtonGroup
      exclusive
      size={size}
      value={value || null}
      // A null value means the user clicked the already-selected button; keep
      // the current selection rather than dropping into an empty state.
      onChange={(_, next) => next !== null && onChange(next)}
      sx={{
        flexWrap: "wrap",
        backgroundColor: "background.subtle",
        borderRadius: 2,
        p: 0.5,
        gap: 0.5,
        "& .MuiToggleButtonGroup-grouped": {
          border: 0,
          borderRadius: "6px !important",
          height: CONTROL_HEIGHT - 8,
          px: 1.75,
          fontSize: "0.8125rem",
          fontWeight: 600,
          color: "text.secondary",
          "&.Mui-selected": {
            backgroundColor: "background.paper",
            color: "text.primary",
            boxShadow: (t) => t.shadows[1],
            "&:hover": { backgroundColor: "background.paper" },
          },
        },
        ...sx,
      }}
    >
      {options.map((option) => (
        <ToggleButton key={option.value} value={option.value}>
          {option.label}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );
}

// Rendered only when something is applied, so the bar stays quiet at rest.
export function ClearFilters({ onClick, count }) {
  if (!count) return null;

  return (
    <Button
      onClick={onClick}
      size="small"
      startIcon={<CloseIcon sx={{ fontSize: 16 }} />}
      sx={{ height: CONTROL_HEIGHT, color: "text.secondary", px: 1.5 }}
    >
      Clear {count > 1 ? `(${count})` : ""}
    </Button>
  );
}

export default FilterBar;
